import React, { useCallback, useMemo } from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';
import { CustomEventNames, EventTypes, logEvent } from 'features/analytics';

import {
  IGetUserMinimalOrdersQuery,
  IGetUserMinimalOrdersQueryVariables,
  IUserMinimalOrderFragment,
  useGetUserMinimalOrdersQuery,
} from 'generated/graphql-gateway';
import { UserOrdersDefaultVariables } from 'hooks/use-user-orders';
import { useAuthContext } from 'state/auth';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import logger from 'utils/logger';

import { mapMinimalOrderToAccountOrder } from './minimal-order-utils';
import { MinimalAccountOrder } from './types';

export const useMinimalAccountOrders = (
  enabled: boolean,
  variables: IGetUserMinimalOrdersQueryVariables,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'
) => {
  const { isAuthenticated } = useAuthContext();
  const {
    indexedMenu,
    indexedOffers,
    indexedRewards,
    isLoading: isUnifiedMenuLoading,
  } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const [fetchedMoreData, setFetchedMoreData] = React.useState<IGetUserMinimalOrdersQuery>();
  const [fetchingMore, setFetchingMore] = React.useState(false);

  const { data, error, loading, refetch, fetchMore } = useGetUserMinimalOrdersQuery({
    variables: {
      ...UserOrdersDefaultVariables,
      ...variables,
    },
    context: { shouldNotBatch: true },
    skip: !enabled || !isAuthenticated,
    fetchPolicy,
  });

  const ordersData = fetchedMoreData ?? data;

  const orders = useMemo<MinimalAccountOrder[]>(() => {
    if (!enabled || isUnifiedMenuLoading) {
      return [];
    }

    return (
      ordersData?.userMinimalOrders.orders?.reduce<MinimalAccountOrder[]>((acc, order) => {
        if (!order) {
          return acc;
        }

        try {
          acc.push(
            mapMinimalOrderToAccountOrder({
              order: order as IUserMinimalOrderFragment,
              menu: indexedMenu,
              indexedOffers,
              indexedRewards,
              posServiceMode,
            })
          );
        } catch (mappingError) {
          logger.warn({
            message: '[useMinimalAccountOrders] Failed to map minimal account order',
            context: {
              rbiOrderId: order.rbiOrderId,
              orderNumberFormatted: order.orderNumberFormatted,
              menuSelectionCount: order.cart.menuSelections?.length ?? 0,
            },
            error: mappingError,
          });
        }

        return acc;
      }, []) ?? []
    );
  }, [
    enabled,
    indexedMenu,
    indexedOffers,
    indexedRewards,
    isUnifiedMenuLoading,
    ordersData?.userMinimalOrders.orders,
    posServiceMode,
  ]);

  const resumeAfter = ordersData?.userMinimalOrders.resumeAfter;
  const count = ordersData?.userMinimalOrders.count || 0;

  const handleLoadMore = useCallback(async () => {
    if (!resumeAfter) {
      return;
    }

    logEvent(CustomEventNames.LOAD_MORE, EventTypes.Other, {
      'Source Page': '/account/orders',
    });

    setFetchingMore(true);

    try {
      const { data: fetchMoreResult } = await fetchMore({
        variables: { resumeAfter },
      });

      const prevOrders = ordersData?.userMinimalOrders.orders || [];
      const currentOrders = fetchMoreResult.userMinimalOrders.orders || [];

      const nextOrdersData = {
        ...fetchMoreResult,
        userMinimalOrders: {
          ...fetchMoreResult.userMinimalOrders,
          orders: [...prevOrders, ...currentOrders],
        },
      };

      setFetchedMoreData(nextOrdersData);

      return nextOrdersData;
    } catch (error) {
      logger.warn({
        message: '[useMinimalAccountOrders] Failed to fetch more minimal account orders',
        error,
      });
    } finally {
      setFetchingMore(false);
    }
  }, [fetchMore, ordersData?.userMinimalOrders.orders, resumeAfter]);

  return {
    orders,
    error,
    isLoading: enabled ? loading || isUnifiedMenuLoading : false,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
  };
};
